/**
 * Pasivos Page — Card-based UI
 * src/pages/Pasivos.jsx
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { showToast } from '../components/Toast';
import { logActivity } from '../utils/auditLog';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

const SEED_PASIVOS = [
  { person: 'Tumi',    tipo: 'Préstamo Nómina 1',   monto: 400000,  pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Préstamos Nómina 2',  monto: 900000,  pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Amex',                 monto: 20000,   pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Like U',               monto: 6000,    pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Platino Mastercard',   monto: 6000,    pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Tumi',                 monto: 900000,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'BBVA Créditos (2)',    monto: 200000,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'MercadoPago',          monto: 115000,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Amex Saldo',           monto: 218861,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Amex Diferidos',       monto: 316927,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Amex MSI',             monto: 62582,   pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Like U',               monto: 9770,    pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'BBVA Visa',            monto: 8700,    pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Hey',                  monto: 380,     pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Nu',                   monto: 520,     pagoMensual: 0, meses: 0 },
];

async function seedPasivos(db) {
  const snap = await getDocs(collection(db, 'pasivos'));
  if (snap.empty) {
    for (const item of SEED_PASIVOS) await addDoc(collection(db, 'pasivos'), item);
    const newSnap = await getDocs(collection(db, 'pasivos'));
    return newSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

const personColor = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

const BLANK_MODAL = { id: null, person: 'Tumi', tipo: '', monto: 0, pagoMensual: 0, meses: 0, saving: false };

export default function Pasivos({ db, user }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  useEffect(() => {
    (async () => {
      try { setRows(await seedPasivos(db)); }
      catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  const totalPasivos      = rows.reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const totalTumiPasivos  = rows.filter(r => r.person === 'Tumi').reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const totalAlbertoPasivos = rows.filter(r => r.person === 'Alberto').reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);

  const openAdd  = () => setModal({ ...BLANK_MODAL });
  const openEdit = (row) => setModal({
    id: row.id, person: row.person, tipo: row.tipo || '',
    monto: row.monto || 0, pagoMensual: row.pagoMensual || 0,
    meses: row.meses || 0, saving: false,
  });

  const handleSave = async () => {
    if (!modal) return;
    setModal(m => ({ ...m, saving: true }));
    try {
      const data = {
        person:      modal.person,
        tipo:        modal.tipo,
        monto:       parseFloat(modal.monto) || 0,
        pagoMensual: parseFloat(modal.pagoMensual) || 0,
        meses:       parseFloat(modal.meses) || 0,
      };
      if (modal.id) {
        await updateDoc(doc(db, 'pasivos', modal.id), data);
        setRows(prev => prev.map(r => r.id === modal.id ? { ...r, ...data } : r));
        await logActivity(db, user, 'editar', 'Pasivo', { tipo: data.tipo, monto: data.monto, person: data.person });
        showToast('Pasivo actualizado');
      } else {
        const ref = await addDoc(collection(db, 'pasivos'), data);
        setRows(prev => [...prev, { id: ref.id, ...data }]);
        await logActivity(db, user, 'crear', 'Pasivo', { tipo: data.tipo, monto: data.monto, person: data.person });
        showToast('Pasivo agregado');
      }
      setModal(null);
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setModal(m => ({ ...m, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!modal?.id) return;
    try {
      await deleteDoc(doc(db, 'pasivos', modal.id));
      setRows(prev => prev.filter(r => r.id !== modal.id));
      await logActivity(db, user, 'eliminar', 'Pasivo', { tipo: modal.tipo, monto: modal.monto, person: modal.person });
      setModal(null);
      showToast('Pasivo eliminado');
    } catch (err) {
      console.error(err);
      showToast('Error al eliminar', 'error');
    }
  };

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  return (
    <div className="page-container">
      {/* Title + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Pasivos <span style={mxnBadge}>MXN</span></h1>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 0.875rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          <Plus size={16} /> Agregar
        </button>
      </div>

      {/* Summary cards */}
      <div className="summary-cards" style={{ marginBottom: '1rem' }}>
        <div className="summary-card">
          <div className="summary-card-label">Total Pasivos</div>
          <div className="summary-card-value" style={{ color: '#ef4444' }}>{fmt(totalPasivos)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label" style={{ color: '#8b5cf6' }}>Tumi Pasivos</div>
          <div className="summary-card-value" style={{ color: '#8b5cf6' }}>{fmt(totalTumiPasivos)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label" style={{ color: '#3b82f6' }}>Alberto Pasivos</div>
          <div className="summary-card-value" style={{ color: '#3b82f6' }}>{fmt(totalAlbertoPasivos)}</div>
        </div>
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Sin pasivos. Agrega uno con el botón +.
          </div>
        )}

        {rows.map(row => (
          <div key={row.id} onClick={() => openEdit(row)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: '1px solid var(--border-color)', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}>
            {/* Person color bar */}
            <div style={{ width: 4, height: 50, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />
            {/* Center info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person), marginBottom: '0.15rem' }}>{row.person}</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.tipo}</div>
              <div style={{ fontSize: '0.73rem', color: 'var(--text-secondary)', marginTop: '0.1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(row.pagoMensual || 0) > 0 && <span>Pago: {fmt(row.pagoMensual)}/mes</span>}
                {(row.meses || 0) > 0 && <span style={{ color: 'var(--text-light)' }}>{row.meses} meses restantes</span>}
              </div>
            </div>
            {/* Right: monto */}
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ef4444' }}>{fmt(row.monto)}</div>
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              {modal.id ? 'Editar' : 'Agregar'} Pasivo
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>Persona</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${modal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: modal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo */}
              <div>
                <label style={labelStyle}>Tipo</label>
                <input type="text" value={modal.tipo} onChange={e => setModal(m => ({ ...m, tipo: e.target.value }))} placeholder="Ej. Amex, Préstamo Nómina..." style={inputStyle} />
              </div>

              {/* Monto */}
              <div>
                <label style={labelStyle}>Monto (MXN)</label>
                <input type="number" inputMode="decimal" value={modal.monto} onChange={e => setModal(m => ({ ...m, monto: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right', color: '#ef4444' }} />
              </div>

              {/* Pago Mensual + Meses */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Pago Mensual</label>
                  <input type="number" inputMode="decimal" value={modal.pagoMensual} onChange={e => setModal(m => ({ ...m, pagoMensual: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Meses Restantes</label>
                  <input type="number" inputMode="numeric" value={modal.meses} onChange={e => setModal(m => ({ ...m, meses: e.target.value }))} placeholder="0" style={{ ...inputStyle, textAlign: 'right' }} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {modal.id && (
                <button onClick={handleDelete}
                  style={{ padding: '0.7rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Trash2 size={15} /> Eliminar
                </button>
              )}
              <button onClick={() => setModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                Cancelar
              </button>
              <button onClick={handleSave} disabled={modal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: modal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: modal.saving ? 0.7 : 1 }}>
                {modal.saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
